'use client';

import dynamic from 'next/dynamic';
import Link from '~/(tenant)/_components/tenant-link';
import { useMemo } from 'react';
import { CalendarPlus } from 'lucide-react';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import type { JWTUserData } from '@kit/supabase/types';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { UserAccountDropdown } from './user-account-dropdown';
import type { NavTabsConfig } from '~/(tenant)/_lib/schema/landing-config.schema';
import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';
import { useSession, useUserRole, useTeamAccount } from '~/lib/hooks/use-header-auth';

const ModeToggle = dynamic(() =>
  import('@kit/ui/mode-toggle').then((mod) => ({
    default: mod.ModeToggle,
  })),
);

const MobileModeToggle = dynamic(() =>
  import('@kit/ui/mobile-mode-toggle').then((mod) => ({
    default: mod.MobileModeToggle,
  })),
);

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

export function SiteHeaderAccountSection({ hideBookingAction = false, compactBookingAction = false, accountMenuTabs }: { hideBookingAction?: boolean; compactBookingAction?: boolean; accountMenuTabs?: NavTabsConfig } = {}) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  // Convert session user to JWTUserData format
  const jwtUserData = useMemo((): JWTUserData | null => {
    const user = session.data?.user;
    if (!user) return null;

    // User type from getSession doesn't include aal/amr, but JWTUserData requires them
    // These are typically available from the JWT claims, so we provide safe defaults
    const userWithExtras = user as typeof user & { aal?: string; amr?: Array<{ method: string; timestamp: number }> };

    return {
      id: user.id,
      email: user.email ?? '',
      phone: user.phone ?? '',
      is_anonymous: user.is_anonymous ?? false,
      aal: (userWithExtras.aal as 'aal1' | 'aal2') ?? 'aal1',
      amr: userWithExtras.amr ?? [],
      app_metadata: user.app_metadata ?? {},
      user_metadata: user.user_metadata ?? {},
    };
  }, [session.data?.user]);

  // If user is authenticated
  if (session.data && jwtUserData) {
    const role = userRole.data || 'guest';

    // Owner/Member roles - show admin access
    if (role === 'owner' || role === 'member') {
    return (
        <div className={'animate-in fade-in flex gap-x-2.5 duration-500'}>
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <div className={'flex gap-x-2.5'}>
            {/* Book Court button - visible on all screen sizes as priority */}
            {!hideBookingAction && <Button asChild variant={'outline'} className={`bg-transparent text-sm px-4 py-1.5 rounded-full`}>
              <Link href={paths.bookCourt}>
                <Trans i18nKey={'common:bookNow'} />
              </Link>
            </Button>}

            <Button asChild variant={'secondary'} className={`hidden md:flex text-sm px-4 py-1.5 rounded-full`}>
              <Link href={teamAccount.data?.slug ? `/home/${teamAccount.data.slug}/dashboard` : '/home/settings'}>
                <Trans i18nKey={'common:adminPanel'} defaults="Admin Panel" />
              </Link>
            </Button>

      <PersonalAccountDropdown
        showProfileName={false}
        paths={paths}
        features={features}
        user={jwtUserData}
        signOutRequested={() => signOut.mutateAsync()}
        className='text-foreground z-50'
      />
          </div>
        </div>
    );
  }

    // User role - show profile only (no admin access)
    if (role === 'user') {
      return (
        <div className={'animate-in fade-in flex gap-x-2.5 duration-500'}>
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          {!hideBookingAction && <BookingAction compact={compactBookingAction} />}

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            menuTabs={accountMenuTabs}
            user={jwtUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className='text-foreground z-50'
          />
        </div>
      );
    }
  }

  // Guest users - show auth buttons
  return <AuthButtons hideBookingAction={hideBookingAction} compactBookingAction={compactBookingAction} />;
}

function BookingAction({ compact }: { compact: boolean }) {
  return <div className="flex gap-x-2.5">
    <Button asChild variant="outline" className={`bg-transparent text-sm ${compact ? 'px-2 md:px-4' : 'px-4'} py-1.5 rounded-full`}>
      <Link href={paths.bookCourt}>
        {compact ? <>
          <CalendarPlus className="h-4 w-4 md:hidden" aria-hidden="true" />
          <span className="sr-only md:not-sr-only"><Trans i18nKey="common:bookNow" /></span>
        </> : <Trans i18nKey="common:bookNow" />}
      </Link>
    </Button>
  </div>;
}

function AuthButtons({ hideBookingAction, compactBookingAction }: { hideBookingAction: boolean; compactBookingAction: boolean }) {
  return (
    <div className={'animate-in fade-in flex gap-x-2.5 duration-500'}>
      {!hideBookingAction && <BookingAction compact={compactBookingAction} />}

      <div className={'hidden md:flex'}>
        <If condition={features.enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className={'md:hidden'}>
        <If condition={features.enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      {/* Use <a> tag for hard navigation to avoid hydration mismatch between route groups */}
      <div className={'flex gap-x-2.5'}>
        <Button
          asChild
          className={`bg-secondary text-primary hover:bg-secondary/90 text-sm px-5 py-1.5 rounded-full`}
          variant={'default'}
        >
          <a href={pathsConfig.auth.signIn}>
            <Trans i18nKey={'auth:signIn'} defaults="Sign In" />
          </a>
        </Button>
      </div>
    </div>
  );
}
